import { useEffect, useRef, useState } from 'react';
import { DBRequest, useConfig } from '../../api/api';
import { Box, useTheme } from '@mui/material';
import { GantDay } from './gant/GantDay';
import { getTimelineDays } from './gant/getTimelineDays';
import { GantPhase } from './gant/GantPhase';
import { updateOrCreate } from '../special/updateOrCreate';
import { ProjectAddElementButton } from './project/ProjectAddElementButton';
import { tokens } from '../../global/theme/tokens';
import { ExoSkeleton } from './ExoSkeleton';

export const ExoGant = ({ projectId, inEditMode, resolution = 1 }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const [apiData, setApiData] = useState(null);
  const [days, setDays] = useState(null);

  useEffect(() => {
    DBRequest({
      config,
      path: `projects/${projectId}/phases`,
      onResponse: handleResponse,
    });
  }, [config]);

  function handleResponse(res) {
    setApiData(res);
  }

  useEffect(() => {
    if (apiData) setDays(getTimelineDays(apiData.data));
  }, [apiData]);

  function handlePhaseChange(newPhase) {
    setApiData(updateOrCreate(apiData, newPhase));
  }

  // responsive
  const containerRef = useRef(null);
  const dayRef = useRef(null);
  const [dayWidth, setDayWidth] = useState(80); // default

  function handleResize() {
    if (dayRef.current) {
      setDayWidth(dayRef.current.clientWidth);
    }
  }

  useEffect(() => {
    // Attach event listener for window resize
    window.addEventListener('resize', handleResize);

    // Initial setup
    handleResize();

    // Cleanup the event listener when the component is unmounted
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [dayRef, days]);

  if (!days || !apiData)
    return (
      <Box
        className="backdrop-blur-md rounded-lg flex flex-col h-full overflow-hidden"
        sx={{ bgcolor: colors.glass }}
      >
        <ExoSkeleton n={1} size="56px" borderRadius="0px" />
        <Box className="py-2 flex flex-col px-1 gap-1">
          <Box className="pb-2">
            <ExoSkeleton n={1} size="23px" width="180px" />
          </Box>
          <Box className=" pl-[0%] pr-[75%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
          <Box className=" pl-[20%] pr-[60%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
          <Box className=" pl-[40%] pr-[20%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
          <Box className=" pl-[80%] pr-[0%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
        </Box>
        <Box className="py-2 flex flex-col px-1 gap-1 pt-2">
          <Box className="pb-2">
            <ExoSkeleton n={1} size="23px" width="180px" />
          </Box>
          <Box className=" pl-[0%] pr-[35%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
          <Box className=" pl-[60%] pr-[10%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
        </Box>
      </Box>
    );

  return (
    <Box className="backdrop-blur-md rounded-lg overflow-hidden flex flex-col h-full">
      <Box
        ref={containerRef}
        className="grid grid-flow-col overflow-x-auto overflow-y-hidden h-full relative"
        sx={{
          gridAutoColumns: 'minmax(80px, 1fr)',
        }}
      >
        {days?.map(
          (day, index) =>
            index % resolution === 0 && (
              <GantDay
                key={day.date.format('YYYY-MM-DD')}
                dayRef={index === 0 ? dayRef : null}
                day={day}
                isFirst={index === 0}
                isLast={index === days.length - 1}
              />
            )
        )}
        <Box
          className="h-full absolute bottom-1 left-0 pt-[65px] z-[5] overflow-y-auto "
          sx={{
            width: Boolean(dayWidth && days?.length)
              ? (dayWidth * days?.length) / resolution
              : 0,
          }}
        >
          {apiData?.data.map((phase, index) => (
            <GantPhase
              key={phase.id}
              phase={phase}
              isFirst={index === 0}
              dayWidth={dayWidth}
              days={days}
              onChange={handlePhaseChange}
              inEditMode={inEditMode}
              projectId={projectId}
              resolution={resolution}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
